import { FC } from 'react';

import Chip from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { Stack } from '@mui/material';
import { UserModel } from '@/lib/models/user.model';
import { GroupMeta } from '@/lib/models/group.model';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { StaticDashboard } from '@/components/metabase/StaticDashboard';
import useMetabase from '@/hooks/use-metabase';

interface Props {
  user: UserModel;
  groups: GroupMeta[];
  policies: PolicyModelV1[];
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Email', prop: 'email' },
  { label: 'Workforce AI Security Groups', prop: 'adminGroups' },
  { label: 'Policies', prop: 'policies' },
  { label: 'Sync Groups', prop: 'syncGroups', rowGroup: 'sync' },
  { label: 'Source', prop: 'source', rowGroup: 'sync' },
  { label: 'Quick Insights', prop: 'insightsSection', propType: 'section' },
  { label: '', prop: 'insights' },
];

const getReadonlyData = (
  data: UserModel | null,
  groups: GroupMeta[],
  policies: PolicyModelV1[],
  dashboardId: string
) => {
  if (!data) {
    return {};
  }

  const { sourceLabel, email, name, isSourceSystem } = data;
  const syncGroups = groups.filter(({ isExternal }) => isExternal);
  const adminGroups = groups.filter(({ isExternal }) => !isExternal);

  return {
    name: name || <NoneChip />,
    email,
    adminGroups: adminGroups.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {adminGroups.map(({ id, name }) => (
          <Chip key={id} label={name} />
        ))}
      </Stack>
    ) : (
      <NoneChip />
    ),
    syncGroups: syncGroups.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {syncGroups.map(({ id, name }) => (
          <Chip key={id} label={name} />
        ))}
      </Stack>
    ) : (
      <NoneChip />
    ),
    source: isSourceSystem ? <NoneChip system /> : sourceLabel,
    policies: policies.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {policies.map(({ id, name }) => (
          <Chip key={`policy-${id}`} label={name} />
        ))}
      </Stack>
    ) : (
      <NoneChip />
    ),
    insights: (
      <StaticDashboard
        dashboardId={dashboardId}
        initialParameters={{ email }}
        hiddenParameters={['email', 'group']}
        isDrawer
      />
    ),
  };
};

export const UserProfile: FC<Props> = ({ user, groups, policies }) => {
  const { dashboards } = useMetabase();
  const { id: dashId } = dashboards.userGroupSmall;
  const dashboardId = dashId || '';

  const data = getReadonlyData(user, groups, policies, dashboardId);

  return <ReadonlyView fields={fields} data={data} />;
};

export default UserProfile;
